@use '../../scss/global' as *;
@use '../../scss/components' as *;

@use 'themes';

// ================================================================ //
// -- Local Variables

$collapse-toggle-padding: 0.75em; // Padding for collapse toggle

// ================================================================ //
// -- Component Styles

:host {
  display: block;

  .fluid-card {
    position: relative;

    &:not(.layout-only) {
      background-color: $white;
      border-width: 0;
      border-style: solid;
      border-color: var(--border-color);
      border-radius: var(--G-border-radius);
      margin-bottom: var(--margin-bottom);

      // == Border (Optional)

      &.card-border {
        border-width: var(--G-border-width);
      }

      // == Drop Shadow (Optional)

      &.card-shadow {
        box-shadow: var(--box-shadow);
      }
    }

    // == Remove Bottom Margin

    &.no-margin {
      margin-bottom: 0;
    }

    // == Full Height

    &.full-height {
      height: 100%;
      max-height: 100%;
      display: flex;
      flex-direction: column;

      .card-header {
        flex: 0 1 auto;
      }

      .card-body {
        flex: 1 1 auto;
      }
    }

    // ==== Header

    .card-header {
      display: flex;
      flex-wrap: wrap;
      position: relative;
      column-gap: 1.25em; // Space between .header-left/center/right
      padding-top: var(--header-padding-top);
      padding-bottom: var(--header-padding-bottom);
      padding-left: var(--padding-left);
      padding-right: var(--padding-right);
      background-color: var(--header-background-color);
      border-top-left-radius: calc(var(--G-border-radius) - 1px);
      border-top-right-radius: calc(var(--G-border-radius) - 1px);

      &.no-wrap {
        flex-wrap: nowrap;
      }

      // == Equalize Top/Bottom Padding When Collapsed

      &.card-collapsed {
        padding-bottom: var(--header-padding-top);
      }

      // == Header Bottom Border

      &.header-border {

        &::after {
          height: var(--G-border-width);
          display: block;
          position: absolute;
          bottom: 0;
          left: var(--header-border-offset);
          right: var(--header-border-offset);
          background-color: var(--border-color);
          content: "";
        }
      }
    }

    // ==== Header Sections (Left, Center, & Right)

    .header-left,
    .header-center,
    .header-right {
      min-height: var(--header-min-height);
      display: flex;
      align-items: center;
      color: inherit;
      column-gap: var(--header-column-gap); // Space between individual controls
    }

    .header-left {
      flex: 0 1 auto;
    }

    .header-center {
      flex: 1 1 auto;
      align-items: center;
    }

    .header-right {
      flex: 0 1 auto;
      margin-left: auto;
    }

    // ==== Title

    .card-title {
      min-height: var(--header-min-height);
      display: flex;
      flex-direction: column;
      justify-content: center;
      font-size: inherit;

      &.collapsible {
        cursor: pointer;
      }
    }

    // == Container for Title, Title Label, & Badge

    .title-container {}

    // == Title & Title Label

    .title-label,
    .title-text {
      vertical-align: middle;
      font-size: var(--title-font-size);
      font-weight: var(--title-font-weight);
      line-height: 1.4;
      margin: 0;
    }

    .title-label {
      & + .title-text {
        font-weight: $font-weight-bold;
      }
    }

    // == Badge

    .title-badge {
      vertical-align: middle;
      line-height: 1;
      margin-left: 1em;
    }

    // == Subtitle

    .card-subtitle {
      display: block;
      font-size: var(--subtitle-font-size);
      font-weight: $font-weight-regular;
      line-height: 1.5em;
      color: var(--G-color-text-medium);
      margin: 0.1em 0;
    }

    // ==== Collapse Toggle

    .collapse-toggle {
      @include button-reset;
      width: var(--collapse-toggle-width);
      height: var(--header-min-height);
      display: flex;
      justify-content: center;
      align-items: center;
      padding-left: $collapse-toggle-padding;
      padding-right: $collapse-toggle-padding;
      border-radius: var(--collapse-toggle-border-radius);
      color: inherit;

      @include transition(
        $properties: 'background-color, color, fill',
        $duration: short,
      );

      &:hover,
      &:focus-visible {
        background-color: $liberty-gray-4;
      }

      &.position-left {
        margin-left: -$collapse-toggle-padding;
      }

      &.position-right {
        margin-right: -$collapse-toggle-padding;
      }
    }

    .collapse-toggle-icon {
      display: block;
      color: var(--collapse-toggle-fill);
    }

    // ==== Refresh Toggle

    .refresh-toggle {
      align-self: center;
      color: var(--refresh-icon-color);

      &:hover,
      &:focus-within {
        color: var(--refresh-icon-color-hover);
      }
    }

    // ==== Body

    .card-body {

      // == Expanded State

      &.card-expanded {
        opacity: 1;
        height: auto;
        overflow: visible;
        transition: opacity 200ms ease-in;
      }

      // == Collapsed State

      &.card-collapsed {
        opacity: 0;
        height: 0;
        overflow: hidden;
        transition: opacity 200ms ease-out;
      }

      // == Full-Height Card

      &.full-height {
        min-height: 0; // Fix flexbox overflow issue
        display: flex;
        flex-direction: column;

        .card-content {
          flex: 1 1 auto;
          min-height: 0; // Fix flexbox overflow issue
        }

        .card-actions {
          flex: 0 1 auto;
        }
      }
    }

    // ==== Body Content Container

    .card-content {
      // Card will not collapse fully if padding applied to .card-body
      padding-top: var(--body-padding-top);
      padding-bottom: var(--body-padding-bottom);
      padding-left: var(--padding-left);
      padding-right: var(--padding-right);
    }

    // ==== Card Actions Slot

    .card-actions {
      display: flex;
      // No bottom padding because Buttons contain bottom margin
      padding-left: var(--padding-left);
      padding-right: var(--padding-right);
    }

    // ==== Remove Padding (applied to .card-content & .card-actions)

    .no-padding {
      padding: 0;
    }

    .no-vertical-padding {
      padding-top: 0;
      padding-bottom: 0;
    }
  }
}

// ==== Full Height Card

:host(.fluid-card--full-height) {
  height: 100%;
}
